import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { FileText, Heart, BarChart, MessageSquare, History, Brain, Wand } from "lucide-react";
import { JobDetailHeader } from "@/components/jobs/JobDetailHeader";
import { JobOverviewCard } from "@/components/jobs/JobOverviewCard";
import { TopMatchesCard } from "@/components/jobs/TopMatchesCard";
import { JobDetailSkeleton } from "@/components/jobs/JobDetailSkeleton";
import { JobNotFound } from "@/components/jobs/JobNotFound";
import { JobKanban } from "@/components/jobs/JobKanban";
import { useJobDetail } from "@/hooks/jobs/useJobDetail";
import { JobAnalysisCard } from "@/components/jobs/JobAnalysisCard";
import { useSettings } from "@/providers/SettingsProvider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { JobComments } from "@/components/jobs/JobComments";
import { JobHistory } from "@/components/jobs/JobHistory";
import ReactMarkdown from 'react-markdown';
import { supabase } from "@/integrations/supabase/client";
import { useJobTopMatches } from "@/hooks/jobs/useJobTopMatches";
import { useTranslation } from "react-i18next";

interface AnalysisData {
	summary?: string;
	highlights?: string[];
	skills_required?: string[];
	hard_skills?: {
		explicit?: string[];
		implicit?: string[];
	};
	work_culture?: {
		team_orientation?: number;
		formality?: number;
		work_life_balance?: number;
		notes?: string;
	};
	workload?: {
		intensity?: number;
		overtime_expectation?: number;
		notes?: string;
	};
	innovation?: {
		change_frequency?: number;
		creativity_emphasis?: number;
		notes?: string;
	};
	management?: {
		autonomy?: number;
		feedback_style?: string;
		notes?: string;
	};
	compensation?: {
		mentioned_elements?: string[];
		competitiveness_indicators?: string[];
		notes?: string;
	};
	remote_work?: {
		flexibility?: number;
		notes?: string;
	};
	key_strengths?: string[];
	potential_concerns?: string[];
	overall_recommendation?: string;
	[key: string]: any;
}

const JobDetail = () => {
	const { id } = useParams<{ id: string }>();
	const { job, loading, formattedDate, refetch } = useJobDetail(id);
	const { matches, loading: matchesLoading, error: matchesError } = useJobTopMatches(id, 10);
	const { dateFormat, timeFormat } = useSettings();
	const [activeTab, setActiveTab] = useState("description");
	const { t } = useTranslation();

	// Use a ref to track the initial load to prevent multiple refreshes
	const [initialLoadComplete, setInitialLoadComplete] = useState(false);
	const [isFormatting, setIsFormatting] = useState(false);

	// Refresh data only on initial load or when explicitly triggered by the header component
	useEffect(() => {
		if (id && refetch && !initialLoadComplete) {
			console.log("Job detail page - initial data load for job ID:", id);
			refetch().then(() => {
				setInitialLoadComplete(true);
			});
		}
	}, [id, refetch, initialLoadComplete]);

	// Load specific tab content only when the tab is active to improve performance
	const loadTabContent = (tabValue: string) => {
		return tabValue === activeTab;
	};

	if (loading && !initialLoadComplete) {
		return <JobDetailSkeleton />;
	}

	if (!job) {
		return <JobNotFound />;
	}

	let analysisData: AnalysisData | undefined;
	if (typeof job.ai_analysis === 'string') {
		try {
			analysisData = job.ai_analysis ? JSON.parse(job.ai_analysis) : undefined;
		} catch (error) {
			console.error("Error parsing job analysis:", error);
			analysisData = undefined;
		}
	} else {
		analysisData = job.ai_analysis as AnalysisData | undefined;
	}

	const handleImproveClick = async () => {
		
		if (!job?.description) {
			console.log("No job description available to improve.");
			return;
		}

		setIsFormatting(true);

		try {
			const { data, error } = await supabase.functions.invoke("reformat-text-with-ai", {
				body: { text: job.description },
			});

			if (error) {
				console.error("Error invoking edge function:", error.message);
				return;
			}

			console.log("Improved description:", data.text);

			// if this is a sucess, we can update the supabase table with the new description
			const { error: updateError } = await supabase
				.from("jobs")
				.update({ description: data.text })
				.eq("id", id);

			if (updateError) {
				console.error("Error updating job description:", updateError.message);
			}
			// Refetch the job details to get the updated description
			await refetch();
			
		} catch (err) {
			console.error("Unexpected error:", err);
		} finally {
			setIsFormatting(false);
		}
	};

	return (
		<div className="w-full">
			<JobDetailHeader
				jobId={id}
				refreshData={() => {
					console.log("Manual refresh triggered from header");
					refetch();
				}}
			/>

			{/* Job Title at the very top */}
			<div className="mb-6">
				<h1 className="text-3xl font-bold">{job.title} - {job.company.name}</h1>
			</div>

			{/* Job Overview Card - Full Width */}
			<div className="w-full mb-6">
				<JobOverviewCard
					company={job.company}
					formattedDate={formattedDate}
					matchesCount={matches.length}
					skills={job.skills}
					seniorityLevel={job.seniorityLevel}
					employmentType={job.employmentType}
					contractDuration={job.contractDuration}
					employmentClassification={job.employmentClassification}
					startDate={job.startDate}
					applicationDeadline={job.applicationDeadline}
					jobPostingDate={job.jobPostingDate}
					budget={job.budget}
					budgetFreelance={job.budgetFreelance}
					locationType={job.locationType}
					workplaceBreakdown={job.workplaceBreakdown}
					hardSkills={job.hardSkills}
					softSkills={job.softSkills}
					languageRequirements={job.languageRequirements}
					extraLang={job.extraLang}
					educationLevel={job.educationLevel}
					yearsOfExperience={job.yearsOfExperience}
					cultureTags={job.cultureTags}
					source={job.source}
					assignedRecruiter={job.assignedRecruiter}
					horizontal={true}
				/>
			</div>

			{/* AI Generated Summary - Full Width */}
			<div className="w-full mb-6 bg-white rounded-lg shadow p-6">
				<h3 className="text-lg font-semibold mb-3">{t("jobs.formWizard.aiSummary")}</h3>
				<p className="text-gray-700">
					{job.aiSummary || t("jobs.formWizard.aiSummaryPlaceholder")}
				</p>
			</div>

			{/* Tabs - Full Width */}
			<div className="w-full">
				<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
					<TabsList className="w-full mb-4">
						<TabsTrigger value="description" className="flex-1 flex items-center gap-2">
							<FileText className="h-4 w-4" />
								<span>{t("jobs.formWizard.jobDescription")}</span>
						</TabsTrigger>
						<TabsTrigger value="ai-analysis" className="flex-1 flex items-center gap-2">
							<Brain className="h-4 w-4" />
								<span>{t("candidates.tabs.aiAnalysis")}</span>
						</TabsTrigger>
						<TabsTrigger value="matches" className="flex-1 flex items-center gap-2">
							<Heart className="h-4 w-4" />
								<span>{t("jobs.tabs.topMatches", "Top Matches")}</span>
						</TabsTrigger>
						<TabsTrigger value="process" className="flex-1 flex items-center gap-2">
							<BarChart className="h-4 w-4" />
								<span>{t("candidates.tabs.recruitmentProcess")}</span>
						</TabsTrigger>
						<TabsTrigger value="comments" className="flex-1 flex items-center gap-2">
							<MessageSquare className="h-4 w-4" />
								<span>{t("candidates.tabs.comments")}</span>
						</TabsTrigger>
						<TabsTrigger value="history" className="flex-1 flex items-center gap-2">
							<History className="h-4 w-4" />
								<span>{t("candidates.tabs.history")}</span>
						</TabsTrigger>
					</TabsList>

					<TabsContent value="description" className="bg-white rounded-lg shadow p-6">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-lg font-semibold">{t("jobs.formWizard.jobDescription")}</h3>
							<button
								onClick={handleImproveClick}
								disabled={isFormatting}
								className={`flex items-center gap-2 px-4 py-2 rounded transition ${
									isFormatting ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"
								}`}
							>
								<Wand className="h-4 w-4" />
								{isFormatting ? t("common.loading", "Formatting...") : t("jobs.formWizard.format", "Format")}
							</button>
						</div>
						{job.description ? (
							 <div className="prose max-w-none">
								<ReactMarkdown>{job.description}</ReactMarkdown>
							</div>
						) : (
							<p className="text-gray-500 italic">{t("jobs.formWizard.noDescription", "No description available for this job.")}</p>
						)}
					</TabsContent>

					<TabsContent value="ai-analysis" className="bg-white rounded-lg shadow">
						{loadTabContent("ai-analysis") && (
							analysisData ? (
								<JobAnalysisCard analysis={analysisData} />
							) : (
								<div className="p-6">
									<p className="text-gray-500 italic">{t("jobs.formWizard.noAIAnalysis", "No AI analysis available for this job.")}</p>
								</div>
							)
						)}
					</TabsContent>

					<TabsContent value="matches" className="bg-white rounded-lg shadow p-6">
						{loadTabContent("matches") && (
							matchesLoading ? (
								<div className="flex justify-center items-center p-6">
									<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
								</div>
							) : matchesError ? (
								<div className="p-6">
									<p className="text-red-500">{t("jobs.formWizard.errorLoadingMatches", { error: matchesError })}</p>
								</div>
							) : (
								matches.length > 0 ? (
									<TopMatchesCard matches={matches} />
								) : (
									<div className="p-6 text-center">
										<p className="text-gray-500">{t("jobs.formWizard.noMatches", "No matches found for this job.")}</p>
									</div>
								)
							)
						)}
					</TabsContent>

					<TabsContent value="process">
						{loadTabContent("process") && (
							<div className="bg-white rounded-lg shadow p-6">
								<h3 className="text-lg font-semibold mb-4">{t("candidates.tabs.recruitmentProcess")}</h3>
								<JobKanban jobId={id} skills={job.skills} />
							</div>
						)}
					</TabsContent>

					<TabsContent value="comments">
						{loadTabContent("comments") && (
							<div className="bg-white rounded-lg shadow p-6">
								<JobComments jobId={id} />
							</div>
						)}
					</TabsContent>

					<TabsContent value="history">
						{loadTabContent("history") && (
							<div className="bg-white rounded-lg shadow p-6">
								<JobHistory jobId={id} />
							</div>
						)}
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
};


function formatTextWithBullets(text) {
	const lines = text.split('\n');
	const elements = [];
	let listItems = [];

	lines.forEach((line, index) => {
		const trimmedLine = line.trim();
		if (/^[-*•]\s+/.test(trimmedLine)) {
			listItems.push(<li key={index}>{trimmedLine.replace(/^[-*•]\s+/, '')}</li>);
		} else {
			if (listItems.length > 0) {
				elements.push(<ul key={`ul-${index}`}>{listItems}</ul>);
				listItems = [];
			}
			if (trimmedLine) {
				elements.push(<p key={index}>{trimmedLine}</p>);
			}
		}
	});

	if (listItems.length > 0) {
		elements.push(<ul key="ul-end">{listItems}</ul>);
	}

	return elements;
}

export default JobDetail;
